<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { useBuddyStore } from '@/stores/buddy'
import { useNotificationsStore } from '@/stores/notifications'
import { useTodayStore } from '@/stores/today'
import { useVoiceStore } from '@/stores/voice'
import { createAppStream } from '@/lib/stream'
import { isNative } from '@/lib/native'
import { initNativeApp } from '@/lib/nativeApp'
import AppShell from '@/layouts/AppShell.vue'
import OutdatedView from '@/views/OutdatedView.vue'

const router = useRouter()
const ui = useUiStore()
const auth = useAuthStore()
const voice = useVoiceStore()
const notifications = useNotificationsStore()

// Hlasovky čekající v outboxu se odešlou po načtení session a pokaždé, když se vrátí připojení.
function flushVoice(): void {
  if (auth.isAuthenticated) void voice.flush()
}

watch(
  () => auth.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      void voice.refreshPending()
      flushVoice()
      // Nativně: je-li oprávnění k upozorněním už uděleno, tiše obnoví token zařízení na serveru.
      if (isNative()) void notifications.syncNativePush()
    }
  },
  { immediate: true },
)

// Nativní push přišel, zatímco je aplikace otevřená: obnovit Dnes a Buddyho (bez toastu).
function onPushReceived(): void {
  if (!auth.isAuthenticated) return
  void useTodayStore().refresh()
  void useBuddyStore().load(true)
}

// Klepnutí na nativní push a sdílení do Buddyho (jen v nativní aplikaci, web se nedotkne).
let offNative: () => void = () => undefined
let unmounted = false

// Živé spojení (SSE) běží, dokud je někdo přihlášený do firmy; při odhlášení a změně firmy se zavře a otevře znovu.
const stream = createAppStream()
watch(
  () => (auth.me?.org ? `${auth.me.user.id}:${auth.me.org.id}` : ''),
  (key) => {
    stream.stop()
    if (key) stream.start()
  },
  { immediate: true },
)
onBeforeUnmount(() => stream.stop())

onMounted(() => {
  ui.init()
  window.addEventListener('online', flushVoice)
  void initNativeApp({ router, onPushReceived }).then((off) => {
    if (unmounted) off()
    else offNative = off
  })
})
onBeforeUnmount(() => {
  unmounted = true
  offNative()
  window.removeEventListener('online', flushVoice)
})
</script>

<template>
  <!-- Server odmítl tuhle verzi aplikace (426): místo aplikace jen výzva k aktualizaci. -->
  <OutdatedView v-if="ui.outdated" />
  <AppShell v-else>
    <RouterView />
  </AppShell>
</template>
